import { Card, CardActionArea, CardContent, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX,
  HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX,
  VEHICLE_TYPE_ICONS,
  VEHICLE_TYPE_TEST_IDS
} from "@/src/components/molecules/VehicleSelectCard/vehicleSelectCard.constants";

import { VehicleType } from "@/src/types/types";

interface VehicleSelectCardHomeViewProps {
  selectedVehicleType: VehicleType;
  onVehicleTypeChange: (value: VehicleType) => void;
  vehicleLabel: string;
  vehicleType: VehicleType;
}

const buildCardSx = (isSelected: boolean) => ({
  backgroundColor: isSelected
    ? theme.palette.common.white
    : theme.palette.semantic.secondary.enabled50p,
  transition: "background-color 0.3s, box-shadow 0.3s",
  width: "100%",
  borderRadius: { xs: "1.25rem 1.25rem 0 0" },
  border: `1px solid ${theme.palette.misc.outlineBorder}`,
  borderBottom: "none",
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
  maxHeight: {
    xs: HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX,
    sm: HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  zIndex: 1
});

const buildCardActionAreaSx = (isSelected: boolean) => ({
  height: "100%",
  minWidth: 0,
  display: "flex",
  alignItems: "stretch",
  minHeight: {
    xs: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX}px`,
    sm: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX}px`
  },
  ".MuiCardActionArea-focusHighlight": {
    backgroundColor: "transparent",
    opacity: 0
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${
      isSelected ? theme.palette.common.black : theme.palette.common.white
    }`,
    outlineOffset: "-2px"
  },
  "&.Mui-focusVisible .MuiCardActionArea-focusHighlight": {
    backgroundColor: isSelected
      ? theme.palette.misc.outlineBorder
      : theme.palette.action.selected,
    opacity: 1
  }
});

const cardContentSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  p: 0,
  px: { xs: 0.5, sm: 1.5 },
  py: { sm: 0 },
  gap: { sm: 1.25 },
  minWidth: 0,
  width: "100%",
  minHeight: {
    xs: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX}px`,
    sm: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX}px`
  },
  flex: 1,
  height: "100%",
  "&:last-child": { paddingBottom: 0 }
};

const buildIconSx = (isSelected: boolean) => ({
  width: { xs: 28, sm: 32 },
  height: { xs: 28, sm: 32 },
  fontSize: { xs: 28, sm: 32 },
  color: isSelected ? theme.palette.common.black : theme.palette.common.white,
  flexShrink: 0
});

const buildLabelSx = (isSelected: boolean) => ({
  color: isSelected ? theme.palette.common.black : theme.palette.common.white,
  fontStyle: "normal",
  lineHeight: { xs: 1.2, sm: 1.33 },

  textAlign: "center",
  flexShrink: 1,
  minWidth: 0,
  m: 0,
  maxWidth: "100%",
  overflow: { xs: "hidden", sm: "visible" },
  textOverflow: { xs: "ellipsis", sm: "clip" },
  whiteSpace: { xs: "nowrap", sm: "normal" }
});

const VehicleSelectCardHomeView = (props: VehicleSelectCardHomeViewProps) => {
  const {
    vehicleType,
    selectedVehicleType,
    onVehicleTypeChange,
    vehicleLabel
  } = props;
  const isSelected = selectedVehicleType === vehicleType;
  const VehicleIcon = VEHICLE_TYPE_ICONS[vehicleType];

  return (
    <Card
      raised={isSelected}
      sx={buildCardSx(isSelected)}
      elevation={0}
      data-testid={VEHICLE_TYPE_TEST_IDS[vehicleType]}
    >
      <CardActionArea
        onClick={() => onVehicleTypeChange(vehicleType)}
        sx={buildCardActionAreaSx(isSelected)}
      >
        <CardContent sx={cardContentSx}>
          <VehicleIcon sx={buildIconSx(isSelected)} />
          <Typography variant="h5" sx={buildLabelSx(isSelected)}>
            {vehicleLabel}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default VehicleSelectCardHomeView;
